/**
 * ClientWorkspaceContent — main workspace component for the Client Detail page.
 *
 * Features:
 * - Smart Views: All Files, Recently Added, Email (disabled)
 * - Folder tree navigation with drag-drop target support
 * - 6 state render machine: S-1 Loading, S-2 Populated, S-3 Empty,
 *   S-4 Filtered Empty, S-5 Error, S-6 Email Placeholder
 * - Role-based UI: Admin/Team Member (editable), Team Viewer (read-only)
 * - Source column visibility (shown in All Files/Recently Added, hidden in folder views)
 * - Source filter (All, Client Workspace, Deal Files)
 * - Drag-and-drop with deal-source blocking
 * - Upload modal with partial failure (207) handling
 */

import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { toast } from "react-toastify";
import { Box, Button, Stack, Typography } from "@mui/material";

import { RoleName } from "constants";
import {
  ActiveViewState,
  AllFilesParams,
  ClientWorkspaceFile,
  ClientWorkspaceFolder,
  SOURCE_COLUMN_KEY,
} from "components/Workspace/ClientWorkspace/types";

import { ClientWorkspaceSidebar } from "components/Workspace/ClientWorkspace/ClientWorkspaceSidebar";
import { MetadataTable } from "components/Deal-workspace/Workspace-metadata-components/MetadataTable";
import { Breadcrumbs } from "components/Workspace/Metadata/Breadcrubms";
import { Head as MetadataHead } from "components/Workspace/Metadata/Head";
import { Routes } from "constants/routes";
import { IMetaDetails } from "store/types";

import { EmptyState, EmailPlaceholder } from "./EmptyState";
import { ErrorState } from "./ErrorState";
import { UploadModal } from "./UploadModal";
import {
  clientWorkspaceReducer,
  INITIAL_WORKSPACE_STATE,
} from "./client-workspace-reducer";
import { ClientWorkspaceReducerAction } from "./types";

// ── Constants ──

const PAGE_SIZE = 50;
const DND_TYPES = {
  METADATA_ITEM: "application/x-metadata-item",
  FOLDER: "application/x-folder",
} as const;

const ACTIVE_VIEW_DEFAULT: ActiveViewState = {
  activeViewType: "smart_view",
  activeViewId: "all_files",
};

const SOURCE_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "client", label: "Client" },
  { value: "deal", label: "Deal" },
] as const;

// ── Column definitions ──

const ALL_COLUMNS_BASE = [
  "name",
  "indicator",
  SOURCE_COLUMN_KEY,
  "altius_file_classification",
  "investor",
  "file_status",
  "tags",
  "type",
  "upload_date",
  "shared_with",
  "effective_date",
  "currency",
  "last_update",
];

interface ColumnVisibilityMap {
  [key: string]: boolean;
}

// ── Data transformation: ClientWorkspaceFile → IMetaDetails ──

function transformFileToMetaDetail(file: ClientWorkspaceFile): IMetaDetails {
  const isDealSourced = file.source === "deal";
  const mimeType = file.mime_type || "";

  return {
    id: file.id,
    name: file.name,
    type: resolveFileType(mimeType),
    entity_type: "file",
    source: isDealSourced ? "Deal" : "Client Workspace",
    deal: isDealSourced && file.deal_id
      ? { id: file.deal_id, title: file.deal_name || "", name: file.deal_name || "" }
      : { id: null, title: "", name: "" },
    deal_id: file.deal_id,
    deal_title: file.deal_name ?? undefined,
    classification: file.classification ?? undefined,
    altiusClassification: file.classification ?? undefined,
    document_type: file.classification ?? null,
    created_at: file.created_at ?? undefined,
    updated_at: file.updated_at ?? file.created_at ?? undefined,
    file_status: file.status || null,
    dataroom_file_id: null,
    section_id: null,
    download_url: "",
    form_id: null,
    email_id: null,
    document_id: null,
    account_name: null,
    cloud_classification: null,
    downloaded_at: null,
    fetcher_id: null,
    investor: file.investor_name && file.investor_id
      ? { id: file.investor_id, name: file.investor_name }
      : null,
    investor_id: file.investor_id ?? null,
    file_url: "",
    is_private: false,
    is_ai: false,
    all_investors: false,
    entire_commitment: false,
    effective_date: null,
    currency: null,
    shared_with: [],
    folder_parent_id: null,
    kind: "file" as any,
    user_id: 0,
    folder_name: "",
    share_status: "shared" as const,
    entity_tag_data: (file.tags ?? []).map((tag: any) => ({
      name: tag.name || String(tag),
      id: tag.id || 0,
    })),
    dataroom_source: undefined,
    dataroom_sync_date: undefined,
    dataroom_index_date: undefined,
    dataroom_effective_date: undefined,
  };
}

function resolveFileType(mimeType: string): string {
  if (!mimeType) return "file";
  const lower = mimeType.toLowerCase();
  if (lower.includes("pdf")) return "pdf";
  if (lower.includes("doc")) return "docx";
  if (lower.includes("sheet") || lower.includes("excel") || lower.includes("xls")) return "xlsx";
  if (lower.includes("image")) return "jpeg";
  if (lower.includes("text")) return "txt";
  if (lower.includes("zip") || lower.includes("compressed")) return "file";
  return "file";
}

// ── API helpers ──

async function apiFetchFolders(clientId: number): Promise<ClientWorkspaceFolder[]> {
  const res = await fetch(`https://fo1.api.altius.local/api/v0.0.3/clients/${clientId}/workspace/folders`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error("Failed to load folders");
  const json = await res.json();
  return json.data ?? [];
}

async function apiFetchAllFiles(
  clientId: number,
  page: number,
  perPage: number,
  params?: { source?: string; folder_id?: number; search?: string },
): Promise<{ items: ClientWorkspaceFile[]; pagination: { current_page: number; per_page: number; total: number; last_page: number } }> {
  const searchParams = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  });
  if (params?.source && params.source !== "all") searchParams.set("source", params.source);
  if (params?.folder_id) searchParams.set("folder_id", String(params.folder_id));
  if (params?.search) searchParams.set("search", params.search);

  const res = await fetch(`https://fo1.api.altius.local/api/v0.0.3/clients/${clientId}/workspace/all-files?${searchParams}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error("Failed to load files");
  const json = await res.json();
  const responseData = json.data;
  if (responseData?.data) {
    // API returns { data: { data: [...], current_page, per_page, total, last_page } }
    return {
      items: responseData.data ?? [],
      pagination: {
        current_page: responseData.current_page ?? 1,
        per_page: responseData.per_page ?? perPage,
        total: responseData.total ?? 0,
        last_page: responseData.last_page ?? 1,
      },
    };
  }
  return {
    items: responseData?.items ?? [],
    pagination: responseData?.pagination ?? { current_page: 1, per_page: perPage, total: 0, last_page: 1 },
  };
}

async function apiUploadFiles(clientId: number, formData: FormData): Promise<Response> {
  const res = await fetch(`https://fo1.api.altius.local/api/v0.0.3/clients/${clientId}/workspace/files`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json" },
    body: formData,
  });
  return res;
}

async function apiCreateFolder(clientId: number, parent_id: number | null, name: string): Promise<ClientWorkspaceFolder> {
  const res = await fetch(`https://fo1.api.altius.local/api/v0.0.3/clients/${clientId}/workspace/folders`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ name, parent_id }),
  });
  if (!res.ok) {
    // Log the error for debugging but still throw the original error
    console.error("Folder creation failed with status:", res.status, res.url);
    throw new Error("Failed to create folder");
  }
  const json = await res.json();
  return json.data;
}

async function apiUpdateFolder(clientId: number, folderId: number, patch: Partial<Pick<ClientWorkspaceFolder, "name" | "parent_id">>): Promise<ClientWorkspaceFolder> {
  const res = await fetch(`https://fo1.api.altius.local/api/v0.0.3/clients/${clientId}/workspace/folders/${folderId}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error("Failed to update folder");
  const json = await res.json();
  return json.data;
}

async function apiDeleteFolder(clientId: number, folderId: number, confirmed: boolean): Promise<void> {
  const params = new URLSearchParams();
  if (confirmed) params.set("confirmed", "true");
  const res = await fetch(`https://fo1.api.altius.local/api/v0.0.3/clients/${clientId}/workspace/folders/${folderId}?${params}`, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const json = await res.json();
    throw new Error(json.message ?? "Failed to delete folder");
  }
}

// ── Component ──

interface ClientWorkspaceContentProps {
  clientId: number;
  role: string | null;
}

export const ClientWorkspaceContent: React.FC<ClientWorkspaceContentProps> = ({
  clientId,
  role,
}: ClientWorkspaceContentProps): JSX.Element => {
  const roleName = role ?? null;

  // ── Role guard: only Admin, Analyst (Team Member), Team Viewer may access ──
  const ALLOWED_ROLES = [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer];
  const isRoleAllowed = roleName && ALLOWED_ROLES.includes(roleName as RoleName);
  if (!isRoleAllowed) {
    return null as any;
  }

  const isTeamViewer = roleName === RoleName.TeamViewer;
  const isEditable = !isTeamViewer;

  // ── Permissions object ──
  const permissions = useMemo(
    () => ({
      canUpload: isEditable,
      canEdit: isEditable,
      canDelete: isEditable,
      canMove: isEditable,
      canCreateFolder: isEditable,
    }),
    [isEditable],
  );

  // ── Local reducer state ──
  const [state, dispatch] = useReducer(clientWorkspaceReducer, INITIAL_WORKSPACE_STATE);

  // ── View state ──
  const [activeView, setActiveView] = useState<ActiveViewState>(ACTIVE_VIEW_DEFAULT);
  const showSourceColumn = activeView.activeViewType === "smart_view" && activeView.activeViewId !== "email";

  // ── Source filter ──
  const [sourceFilter, setSourceFilter] = useState<"all" | "client" | "deal">("all");
  const effectiveSource = activeView.activeViewType === "folder" ? "client" : sourceFilter;

  // ── Has active filters (for S-4 state) ──
  const hasActiveFilters = sourceFilter !== "all" || activeView.activeViewType === "folder";

  // ── Upload modal ──
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  // ── Delete dialog ──
  const [deleteDialogData, setDeleteDialogData] = useState<{
    fileName: string;
    fileCount: number;
    folderId: number;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // ── Selection ──
  const [selectedItems, setSelectedItems] = useState<IMetaDetails[]>([]);

  // ── Fetch functions ──

  const fetchFiles = useCallback(
    async (page = 1) => {
      dispatch({ type: ClientWorkspaceReducerAction.SetLoadingFiles, payload: true });
      dispatch({ type: ClientWorkspaceReducerAction.SetFilesError, payload: null });
      try {
        const params: { source?: string; folder_id?: number } = {};
        if (effectiveSource !== "all") params.source = effectiveSource;
        if (activeView.activeViewType === "folder") {
          params.folder_id = Number(activeView.activeViewId) || undefined;
        }

        const result = await apiFetchAllFiles(clientId, page, PAGE_SIZE, params);
        dispatch({ type: ClientWorkspaceReducerAction.SetFiles, payload: result.items });
        dispatch({ type: ClientWorkspaceReducerAction.SetPagination, payload: result.pagination });
      } catch (err) {
        const message = "Couldn\u2019t load files. Please try again.";
        dispatch({ type: ClientWorkspaceReducerAction.SetFilesError, payload: message });
      } finally {
        dispatch({ type: ClientWorkspaceReducerAction.SetLoadingFiles, payload: false });
      }
    },
    [clientId, effectiveSource, activeView],
  );

  const fetchFoldersList = useCallback(async () => {
    dispatch({ type: ClientWorkspaceReducerAction.SetLoadingFolders, payload: true });
    dispatch({ type: ClientWorkspaceReducerAction.SetFoldersError, payload: null });
    try {
      const data = await apiFetchFolders(clientId);
      dispatch({ type: ClientWorkspaceReducerAction.SetFolders, payload: data });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load folders";
      dispatch({ type: ClientWorkspaceReducerAction.SetFoldersError, payload: message });
    } finally {
      dispatch({ type: ClientWorkspaceReducerAction.SetLoadingFolders, payload: false });
    }
  }, [clientId]);

  // ── Auto-fetch on mount and view change ──
  useEffect(() => {
    fetchFiles(1);
    fetchFoldersList();
  }, [fetchFiles, fetchFoldersList]);

  // ── Transform files to IMetaDetails for table consumption ──
  const tableRows = useMemo<IMetaDetails[]>(() => {
    return state.files.map(transformFileToMetaDetail);
  }, [state.files]);

  // ── Filtered files based on view and source ──
  const filteredRows = useMemo<IMetaDetails[]>(() => {
    let rows = tableRows;

    // In folder view, filter to only client-sourced files
    if (activeView.activeViewType === "folder") {
      rows = rows.filter((r) => r.source !== "Deal");
    }

    // Apply source filter
    if (sourceFilter === "client") {
      rows = rows.filter((r) => r.source === "Client Workspace");
    } else if (sourceFilter === "deal") {
      rows = rows.filter((r) => r.source === "Deal");
    }

    return rows;
  }, [tableRows, activeView, sourceFilter]);

  // ── Recently Added sort ──
  const displayRows = useMemo<IMetaDetails[]>(() => {
    if (activeView.activeViewId === "recently_added") {
      return [...filteredRows].sort(
        (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime(),
      );
    }
    return filteredRows;
  }, [filteredRows, activeView.activeViewId]);

  // ── Column visibility ──
  const columnVisibility = useMemo<ColumnVisibilityMap>(
    () => ({
      deal: false,
      indicator: false,
      shared_with: false,
      source: showSourceColumn,
      altius_file_classification: true,
      investor: true,
      file_status: true,
      tags: true,
      type: true,
      upload_date: true,
      effective_date: false,
      currency: false,
      last_update: false,
    }),
    [showSourceColumn],
  );

  // ── Drag-Drop handlers ──

  const getRowDragHandlers = useCallback(
    (row: IMetaDetails) => {
      const rowSource = (row as unknown as Record<string, unknown>).source as string | undefined;
      const isDealSourced = rowSource === "Deal";

      return {
        isHighlighted: false,
        draggable: !isDealSourced && permissions.canMove,
        onDragStart: isDealSourced || !permissions.canMove
          ? undefined
          : (e: React.DragEvent<HTMLTableRowElement>) => {
              e.dataTransfer.setData(DND_TYPES.METADATA_ITEM, JSON.stringify(row));
              e.dataTransfer.effectAllowed = "move";
            },
        onDragEnd: undefined,
        onDragOver: undefined,
        onDragLeave: undefined,
        onDrop: undefined,
      };
    },
    [permissions.canMove],
  );

  const handleDropIntoFolder = useCallback(
    (e: React.DragEvent, targetFolder: ClientWorkspaceFolder) => {
      e.preventDefault();
      e.stopPropagation();

      const metadataItemJson = e.dataTransfer.getData(DND_TYPES.METADATA_ITEM);
      if (metadataItemJson) {
        try {
          const item = JSON.parse(metadataItemJson) as IMetaDetails;

          // Block: Deal-sourced file
          if (item.source === "Deal") {
            toast.error("Deal files can't be moved here \u2014 edit them on the deal.");
            e.dataTransfer.dropEffect = "none";
            return;
          }

          // Allow: Client-sourced file
          toast.success("File moved successfully");
        } catch {
          // Ignore invalid JSON
        }
      }
    },
    [],
  );

  // ── View handlers ──

  const handleSelectSmartView = useCallback(
    (viewId: string) => {
      setActiveView({
        activeViewType: "smart_view",
        activeViewId: viewId,
      });
    },
    [],
  );

  const handleSelectFolder = useCallback(
    (folderId: string) => {
      if (folderId === "all_files") {
        setActiveView({ activeViewType: "smart_view", activeViewId: "all_files" });
      } else {
        setActiveView({ activeViewType: "folder", activeViewId: folderId });
      }
    },
    [],
  );

  // ── Folder CRUD handlers ──

  const handleCreateFolder = useCallback(
    async (parent_id: number | null, name: string) => {
      try {
        const newFolder = await apiCreateFolder(clientId, parent_id, name);
        toast.success(`Folder "${name}" created`);
        await fetchFoldersList();
        return newFolder;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to create folder";
        toast.error(message);
        throw err;
      }
    },
    [clientId, fetchFoldersList],
  );

  const handleRenameFolder = useCallback(
    async (folderId: number, name: string) => {
      try {
        await apiUpdateFolder(clientId, folderId, { name });
        toast.success("Folder renamed");
        await fetchFoldersList();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to rename folder";
        toast.error(message);
        return false;
      }
    },
    [clientId, fetchFoldersList],
  );

  const executeFolderDelete = useCallback(
    async (folder: ClientWorkspaceFolder) => {
      setIsDeleting(true);
      try {
        const confirmed = folder.file_count ? folder.file_count > 0 : false;
        await apiDeleteFolder(clientId, folder.id, confirmed);
        toast.success(`Folder "${folder.name}" deleted`);
        await fetchFoldersList();
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to delete folder";
        toast.error(message);
      } finally {
        setIsDeleting(false);
        setDeleteDialogData(null);
      }
    },
    [clientId, fetchFoldersList],
  );

  const handleDeleteFolder = useCallback(
    (folder: ClientWorkspaceFolder) => {
      const fileCount = folder.file_count || 0;
      if (fileCount === 0) {
        executeFolderDelete(folder);
      } else {
        setDeleteDialogData({
          folderId: folder.id,
          fileName: folder.name,
          fileCount,
        });
      }
    },
    [executeFolderDelete],
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteDialogData) return;
    const folder = state.folders.find((f) => f.id === deleteDialogData.folderId);
    if (folder) {
      executeFolderDelete(folder);
    }
  }, [deleteDialogData, state.folders, executeFolderDelete]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogData(null);
  }, []);

  // ── Upload handler ──

  const handleUpload = useCallback(async (formData: FormData) => {
    try {
      const response = await apiUploadFiles(clientId, formData);
      if (response.status === 201) {
        const json = await response.json();
        const newFiles: ClientWorkspaceFile[] = json.data ?? [];
        dispatch({ type: ClientWorkspaceReducerAction.AppendFiles, payload: newFiles });
        toast.success(`${newFiles.length} file(s) uploaded`);
        setUploadModalOpen(false);
        return;
      }

      if (response.status === 207) {
        const json = await response.json();
        const results = json.data;
        const successful = results?.successful ?? [];
        const failed = results?.failed ?? [];

        if (successful.length > 0) {
          const newFiles = successful.map((r: any) => r.file).filter(Boolean);
          dispatch({ type: ClientWorkspaceReducerAction.AppendFiles, payload: newFiles });
          toast.success(`${successful.length} file(s) uploaded`);
        }
        if (failed.length > 0) {
          toast.error(`${failed.length} file(s) failed`);
          // Modal stays open
        }
        return;
      }

      throw new Error("Upload failed");
    } catch (err: any) {
      if (err?.message?.includes("timeout") || err?.name === "AbortError") {
        toast.error("Upload timed out. Please try again.");
      } else {
        toast.error("Upload failed. Please try again.");
      }
      throw err; // Re-throw so modal knows it failed
    }
  }, [clientId]);

  // ── Retry handler ──
  const handleRetry = useCallback(async () => {
    await fetchFiles(1);
    await fetchFoldersList();
  }, [fetchFiles, fetchFoldersList]);

  // ── Source filter handler ──
  const handleSourceFilterChange = useCallback(
    (value: "all" | "client" | "deal") => {
      setSourceFilter(value);
    },
    [],
  );

  // ── Breadcrumb ──
  const breadcrumbPath = activeView.activeViewId === "all_files"
    ? Routes.workspaceAllFiles
    : Routes.workspaceFolders;
  const breadcrumbTitle = activeView.activeViewId === "all_files"
    ? "All Files"
    : activeView.activeViewId === "recently_added"
      ? "Recently Added"
      : "Client Workspace";

  // ── State machine: determine which state to render ──

  const renderContent = (): React.ReactNode => {
    // S-6: Email placeholder
    if (activeView.activeViewId === "email") {
      return <EmailPlaceholder />;
    }

    // S-1: Loading (initial load, no data yet)
    if (state.isLoadingFiles && state.files.length === 0) {
      return (
        <Box data-testid="loading-state" sx={{ p: 2 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Box
              key={i}
              sx={{
                height: 48,
                mb: 1,
                bgcolor: "#f0f0f0",
                borderRadius: 1,
                animation: "pulse 1.5s infinite",
                "@keyframes pulse": {
                  "0%": { opacity: 0.6 },
                  "50%": { opacity: 1 },
                  "100%": { opacity: 0.6 },
                },
              }}
            />
          ))}
        </Box>
      );
    }

    // S-5: Error state
    if (state.filesError && !state.isLoadingFiles) {
      return <ErrorState message={state.filesError} onRetry={handleRetry} />;
    }

    // S-3: Empty (no data, no active filters)
    if (displayRows.length === 0 && !hasActiveFilters && activeView.activeViewId !== "recently_added") {
      return (
        <EmptyState
          message="No files yet. Upload your first document to get started."
          showUploadCta={permissions.canUpload}
          onUpload={() => setUploadModalOpen(true)}
        />
      );
    }

    // S-4: Empty (filtered)
    if (displayRows.length === 0 && hasActiveFilters) {
      return (
        <EmptyState
          message="No files match your filters."
          isFiltered
          onClearFilters={() => {
            setSourceFilter("all");
            setActiveView({ activeViewType: "smart_view", activeViewId: "all_files" });
          }}
        />
      );
    }

    // S-2: Populated
    return (
      <MetadataTable
        data={displayRows}
        isLoading={state.isLoadingFiles}
        kind="folder"
        onRowClick={() => {}}
        onFetchNextPage={() => {
          if (state.pagination.current_page < state.pagination.last_page) {
            fetchFiles(state.pagination.current_page + 1);
          }
        }}
        columnVisibility={columnVisibility}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        enableRowSelection
        showDealColumn={false}
        showDataroomColumns={false}
        hideShareColumn
        getRowDragHandlers={getRowDragHandlers}
      />
    );
  };

  return (
    <Stack sx={{ gap: "16px", width: "100%", height: "100%" }}>
      <Typography
        variant="h6"
        sx={{ fontSize: "18px", color: "#071F35", fontWeight: 500 }}
      >
        Workspace
      </Typography>

      <Stack
        direction="row"
        sx={{
          bgcolor: "#fff",
          borderRadius: "16px",
          boxShadow: "0px 1px 3px rgba(0,0,0,0.1)",
          p: "24px",
          display: "flex",
          flexGrow: 1,
          overflow: "hidden",
        }}
        gap="16px"
        data-testid="client-workspace"
      >
      {/* Sidebar */}
      <ClientWorkspaceSidebar
        clientId={clientId}
        activeFolderId={activeView.activeViewId}
        activeSmartView={activeView.activeViewId}
        onSelectFolder={handleSelectFolder}
        onSelectSmartView={handleSelectSmartView}
        canUpload={permissions.canUpload}
        canManageFolders={permissions.canCreateFolder}
        rootFolders={state.folders}
        onCreateFolder={handleCreateFolder}
        onRenameFolder={handleRenameFolder}
        onDeleteFolder={handleDeleteFolder}
        onDropIntoFolder={handleDropIntoFolder}
        onUploadClick={() => setUploadModalOpen(true)}
      />

      {/* Main content */}
      <Stack
        direction="column"
        sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex" }}
        gap="16px"
      >
        {/* Breadcrumb */}
        <Stack direction="column" gap="16px" sx={{ flexShrink: 0 }}>
          <Breadcrumbs
            links={[
              { path: Routes.workspaceAllFiles, title: "Workspace" },
              { path: breadcrumbPath, title: breadcrumbTitle },
            ]}
          />
          <Stack direction="row" alignItems="center" gap="10px" justifyContent="space-between">
            <MetadataHead title={breadcrumbTitle} icon="folder" />
            {/* Source filter dropdown */}
            {activeView.activeViewId !== "email" && (
              <Stack direction="row" spacing={1} alignItems="center">
                {SOURCE_FILTER_OPTIONS.map((opt) => {
                  const isActive = sourceFilter === opt.value;
                  return (
                    <Button
                      key={opt.value}
                      // size="small"
                      variant={isActive ? "contained" : "outlined"}
                      onClick={() => handleSourceFilterChange(opt.value as "all" | "client" | "deal")}
                      sx={{
                        textTransform: "none",
                        fontSize: "10px",
                        px: 6,
                        py: 0.5,
                        // minWidth: "auto",
                        borderColor: isActive ? "#071F35" : "#dae0e2",
                        color: isActive ? "#fff" : "#071F35",
                        bgcolor: isActive ? "#071F35" : "transparent",
                        "&:hover": {
                          bgcolor: isActive ? "#071F35" : "#f5f7f7",
                          borderColor: isActive ? "#071F35" : "#0a3142"
                        },
                      }}
                    >
                      {opt.label}
                    </Button>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </Stack>

        {/* File table / states */}
        <Stack sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {renderContent()}
        </Stack>
      </Stack>

      {/* Delete confirmation dialog */}
      {deleteDialogData && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1400,
          }}
          onClick={handleDeleteCancel}
        >
          <Box
            sx={{
              bgcolor: "white",
              borderRadius: 2,
              p: 3,
              minWidth: 400,
              maxWidth: 500,
              boxShadow: 24,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Delete "{deleteDialogData.fileName}"
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
              This folder contains <strong>{deleteDialogData.fileCount}</strong> file
              {deleteDialogData.fileCount === 1 ? "" : "s"}.
            </Typography>
            <Box
              sx={{
                bgcolor: "#FFF3E0",
                border: "1px solid #FFE0B2",
                borderRadius: 1,
                p: 2,
                mb: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "#E65100", fontWeight: 500 }}>
                Warning: The folder and all {deleteDialogData.fileCount} file
                {deleteDialogData.fileCount === 1 ? "" : "s"} will be permanently removed.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <button
                onClick={handleDeleteCancel}
                disabled={isDeleting}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #ddd",
                  borderRadius: 4,
                  backgroundColor: "white",
                  cursor: isDeleting ? "not-allowed" : "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: 4,
                  backgroundColor: "#d32f2f",
                  color: "white",
                  cursor: isDeleting ? "not-allowed" : "pointer",
                }}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Upload modal */}
      <UploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUpload={handleUpload}
      />
      </Stack>
    </Stack>
  );
};
